import React, { useState } from "react";
import { Grid, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { FormattedMessage, useIntl } from "react-intl";

import style from "../styles/index.module.css";

import Modal from "@/components/Modal";
import { InputField } from "@/components/InputField";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import SelectCountry from "@/components/SelectCountry";

import { IButtonType, IButtonVariant } from "@/models/button";
import { IMobileVerify } from "@/models/onboarding";

import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";
import { useGuestLogin } from "@/utils/hooks/useGuestLogin";

interface IProps {
  isBtnLoading: boolean;
  heading: string;
  mobileSubmitHandler: (values: IMobileVerify, countryCode: string) => void;
  btnLabel?: string;
  showCountryCode?: boolean;
  showHeaderNavigation?: boolean;
  headerNavigationText?: string;
  isOpen: boolean;
  closeHandler: () => void;
  mobileNo?: string;
}

const VerifyMobile: React.FC<IProps> = (props) => {
  const [phone, setPhone] = useState({
    number: "",
    countryCode: "",
    mobileLength: "",
  });

  const {
    heading,
    mobileSubmitHandler,
    btnLabel = "getOTP",
    showCountryCode = true,
    showHeaderNavigation = false,
    headerNavigationText,
    isOpen,
    closeHandler,
    isBtnLoading,
  } = props;

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const { guestUserLogin, guestUserLoading } = useGuestLogin();

  const intl = useIntl();

  const handleChangeMobileNo = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/\D/g, "");
    setPhone({
      ...phone,
      number: value,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (phone.number.trim().length === parseInt(phone.mobileLength)) {
      mobileSubmitHandler({ mobileNo: phone.number }, phone.countryCode);
    }
  };

  const mobileInputField = () => {
    return (
      <>
        {showHeaderNavigation && (
          <Grid
            item
            className={style.headerBackNav}
            onClick={() => closeHandler && closeHandler()}
          >
            <ArrowBackIosNewIcon fontSize={"inherit"} />{" "}
            <Typography component={"h3"} className={style.heading}>
              <FormattedMessage
                id={headerNavigationText}
                defaultMessage="headerNavigationText"
              />
            </Typography>
          </Grid>
        )}
        <Grid item className={style.headerContainer}>
          <Typography component={"h3"} className={style.heading}>
            <FormattedMessage
              id={heading}
              defaultMessage="Login using Phone Number"
            />
          </Typography>
        </Grid>
        <form onSubmit={handleSubmit}>
          <Grid item>
            <Grid item className={style.mobileBox}>
              <Grid item className={style.mobileInputContainer}>
                {showCountryCode && (
                  <SelectCountry
                    isSmallScreen={isMobile || isTablet}
                    countryCodeAndMobileLength={`${phone?.countryCode}_${phone?.mobileLength}`}
                    setCountryCodeAndMobileLength={(
                      countryCode: string,
                      mobileLength: string
                    ) => setPhone({ ...phone, countryCode, mobileLength })}
                  />
                )}
                <InputField
                  type="text"
                  inputMode="numeric"
                  inputProps={{
                    maxLength: phone?.mobileLength,
                    pattern: "[0-9]*",
                  }}
                  value={phone.number}
                  onChange={handleChangeMobileNo}
                  label={intl.formatMessage({
                    id: "mobileNumberText",
                    defaultMessage: "Mobile Number",
                  })}
                  variant="outlined"
                  placeholder={intl.formatMessage({
                    id: "mobileNumberText",
                    defaultMessage: "Mobile Number",
                  })}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid item className={style.mobileOTPContainer}>
              <Button
                variant={
                  phone.number.trim().length !== parseInt(phone.mobileLength)
                    ? IButtonVariant.GREY
                    : IButtonVariant.ORANGE
                }
                type={IButtonType.SUBMIT}
                disabled={
                  phone.number.trim().length !== parseInt(phone.mobileLength)
                }
                isLoading={isBtnLoading}
              >
                <FormattedMessage id={btnLabel} defaultMessage="Get OTP" />
              </Button>
              {(isMobile || isTablet) && (
                <Button
                  btnTrigger={guestUserLogin}
                  variant={IButtonVariant.WHITE}
                  isLoading={guestUserLoading}
                >
                  <FormattedMessage id={"skip"} defaultMessage="Skip" />
                </Button>
              )}
            </Grid>
          </Grid>
        </form>
      </>
    );
  };

  return (
    <>
      {isMobile || isTablet ? (
        <Modal
          isOpen={isOpen}
          closeHandler={closeHandler}
          lineBottomBackground="white"
        >
          {mobileInputField()}
        </Modal>
      ) : (
        <Dialog
          fullWidth
          maxWidth="xs"
          isOpen={isOpen}
          closeHandler={closeHandler}
          paperProps={{
            sx: {
              position: "absolute",
              top: 80, // Adjust distance from top
              right: 60, // Adjust distance from right
              margin: 0,
              borderRadius: "1.6rem",
            },
          }}
        >
          {mobileInputField()}
        </Dialog>
      )}
    </>
  );
};

export default VerifyMobile;
